import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';
import { SignedDocument } from 'src/app/classes/models';
import { S3PresignedUrl } from 'src/app/classes/s3-presigned-url';

import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class SignedDocumentService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  getSignedDocument(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<SignedDocument>;
        } = {
          Vendor: () =>
            this.http.get<SignedDocument>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/companyVendor/signedDocument/one`,
              { params: query }
            ),
          Client: () =>
            this.http.get<SignedDocument>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyVendor/signedDocument/one`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<SignedDocument>(
              `/api/v2/admin/companyVendor/signedDocument/one`,
              {
                params: query
              }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  getSignedDocuments(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<SignedDocument[]>;
        } = {
          Vendor: () =>
            this.http.get<SignedDocument[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/companyVendor/signedDocument/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<SignedDocument[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyVendor/signedDocument/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<SignedDocument[]>(
              `/api/v2/admin/companyVendor/signedDocument/many`,
              {
                params: query
              }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  getSignedDocumentCount(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.get(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/companyVendor/signedDocument/count`,
              { params: query }
            ),
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyVendor/signedDocument/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(`/api/v2/admin/companyVendor/signedDocument/count`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  getS3PreSignedUrls(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<[S3PresignedUrl]>;
        } = {
          Vendor: () =>
            this.http.get<[S3PresignedUrl]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/companyVendor/signedDocument/getS3PresignedUrls`,
              { params: query }
            ),
          Client: () =>
            this.http.get<[S3PresignedUrl]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyVendor/signedDocument/getS3PresignedUrls`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<[S3PresignedUrl]>(
              `/api/v2/admin/companyVendor/signedDocument/getS3PresignedUrls`,
              {
                params: query
              }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }

  saveSignedDocument(signedDocument: SignedDocument) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<SignedDocument>;
        } = {
          Admin: () =>
            this.http.post<SignedDocument>(
              `/api/v2/admin/signedDocument`,
              signedDocument
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        this.ToastService.addErrorToast('Error', 'Invalid permission.');
        throw new Error('Invalid permission.');
      })
    );
  }
}
